import { useQuery } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"
import type { paths } from "@/api/schema"

/**
 * Hooks for `backend/app_core/routers/dashboard.py` — the by-buyer /
 * by-account analytics endpoints consumed by the Dashboard page
 * (T3.3). Mirrors the conventions documented in
 * `frontend/src/api/README.md`:
 *
 *   1) Derive the path type from the generated schema so the URL
 *      string stays type-checked against OpenAPI.
 *   2) Query key starts with the router name ("dashboard") so cache
 *      invalidation can scope by router.
 *
 * Deviation note: the FastAPI handlers return a manually-built
 * `JSONResponse` (no `response_model`), so the generated schema
 * declares the body as `unknown`. We therefore define explicit
 * `BuyerAnalyticsRow` / `AccountAnalyticsRow` / `AnalyticsTotals`
 * shapes mirroring `dashboard.py:206-225` and `dashboard.py:422-442`.
 * When those handlers grow Pydantic response models, swap these for
 * the generated row types.
 */

// Compile-time guards: the paths stay in the generated schema. If a
// rename flips back, these fail to typecheck and we know to update
// the calls below.
type _BuyerPath = paths["/api/dashboard/analytics/by-buyer"]["get"]
type _AccountPath = paths["/api/dashboard/analytics/by-account"]["get"]
void (null as unknown as _BuyerPath)
void (null as unknown as _AccountPath)

export interface AnalyticsTotals {
  cost: number
  revenue: number
  profit: number
  impressions: number
  clicks: number
  conversions: number
  gclicks: number
  cpc: number
  cpm: number
  cpa: number
  rpc: number
  roi: number | null
  cvr: number | null
  ctr: number | null
  /** Sum of cached account balances (buyer + account tab totals). */
  balance?: number | null
  active_campaigns?: number | null
  accounts_active?: number | null
  accounts_total?: number | null
}

export interface BuyerAnalyticsRow {
  user_id: number
  username: string
  role: string | null
  balance?: number | null
  active_campaigns?: number | null
  accounts_active?: number | null
  accounts_total?: number | null
  cost: number
  revenue: number
  profit: number
  impressions: number
  clicks: number
  conversions: number
  gclicks: number
  cpc: number
  cpm: number
  cpa: number
  rpc: number
  roi: number | null
  cvr: number | null
  ctr: number | null
}

export interface AccountAnalyticsRow {
  account_id: number
  advertiser_id: string
  account_name: string | null
  network: string | null
  /** TikTok advertiser status (cached on balance refresh). */
  advertiser_status?: string | null
  /** ISO timestamp when the account was soft-deleted; null if still live. */
  deleted_at?: string | null
  /** Cached TikTok account balance (from balance refresh). */
  balance?: number | null
  currency?: string | null
  /** Link to TikTok Ads Manager filtered by advertiser + dashboard date range. */
  tiktok_url?: string | null
  cost: number
  revenue: number
  profit: number
  impressions: number
  clicks: number
  conversions: number
  gclicks: number
  cpc: number
  cpm: number
  cpa: number
  rpc: number
  roi: number | null
  cvr: number | null
  ctr: number | null
}

export interface BuyerAnalyticsResponse {
  success: boolean
  message?: string
  data?: BuyerAnalyticsRow[]
  totals?: AnalyticsTotals | Record<string, never>
}

export interface AccountAnalyticsResponse {
  success: boolean
  message?: string
  data?: AccountAnalyticsRow[]
  totals?: AnalyticsTotals | Record<string, never>
}

export interface DashboardAnalyticsParams {
  start_date: string
  end_date: string
  /** `"all"` → omit query param (matches legacy behavior). */
  network: string
  /** `"all"` is sent explicitly so the backend does not default to active-only. */
  status: string
  /** Optional buyer IDs for media-buyer filtering in both tabs. */
  user_ids?: number[]
  /** @deprecated Dashboard uses local start/end dates only; kept for API compat. */
  reporting_preset?: "today" | "yesterday"
}

/**
 * Serialize filters into the backend query-string contract:
 *  - `start_date` + `end_date` always included
 *  - `network` / `status` only when not `"all"` (legacy parity,
 *    `dashboard.html:2133-2140`)
 */
function buildUrl(
  basePath: string,
  { start_date, end_date, network, status, user_ids, reporting_preset }: DashboardAnalyticsParams,
): string {
  const params = new URLSearchParams({ start_date, end_date })
  if (network && network !== "all") params.append("network", network)
  if (status) params.append("status", status)
  if (reporting_preset) params.append("reporting_preset", reporting_preset)
  if (user_ids && user_ids.length > 0) {
    user_ids.forEach((id) => params.append("user_ids", String(id)))
  }
  return `${basePath}?${params.toString()}`
}

export const DASHBOARD_QUERY_KEYS = {
  all: ["dashboard"] as const,
  byBuyer: (p: DashboardAnalyticsParams) =>
    ["dashboard", "by-buyer", p] as const,
  byAccount: (p: DashboardAnalyticsParams) =>
    ["dashboard", "by-account", p] as const,
} as const

export function useBuyerAnalytics(
  params: DashboardAnalyticsParams,
  options: { enabled?: boolean } = {},
) {
  const enabled = options.enabled ?? true
  return useQuery<BuyerAnalyticsResponse>({
    queryKey: DASHBOARD_QUERY_KEYS.byBuyer(params),
    queryFn: () =>
      apiFetch<BuyerAnalyticsResponse>(
        buildUrl("/api/dashboard/analytics/by-buyer", params),
      ),
    enabled: enabled && Boolean(params.start_date && params.end_date),
    retry: false,
  })
}

export function useAccountAnalytics(
  params: DashboardAnalyticsParams,
  options: { enabled?: boolean } = {},
) {
  const enabled = options.enabled ?? true
  return useQuery<AccountAnalyticsResponse>({
    queryKey: DASHBOARD_QUERY_KEYS.byAccount(params),
    queryFn: () =>
      apiFetch<AccountAnalyticsResponse>(
        buildUrl("/api/dashboard/analytics/by-account", params),
      ),
    enabled: enabled && Boolean(params.start_date && params.end_date),
    retry: false,
  })
}
